import { useQuery } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { api, errorMessage, rawFetch, unwrap, type S } from "../../api/client";
import { Alert, Badge, Card, Empty, PageHead, Segmented, Spinner, cx } from "../../components/ui";
import { FormattedMessage } from "react-intl";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import { useFmt } from "./format";

const STATUS_TONE: Record<S["EmailStatus"], "info" | "ok" | "danger" | "warn"> = {
  queued: "info",
  sent: "ok",
  failed: "danger",
  suppressed: "warn",
};
type StatusFilter = "all" | S["EmailStatus"];

function Preview({ name, brand }: { name: string; brand: "sample" | "none" }) {
  const [html, setHtml] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [error, setError] = useState<string | null>(null);
  const t = useT();
  useEffect(() => {
    let live = true;
    setHtml(null);
    setError(null);
    rawFetch(`/api/v1/admin/email/templates/${encodeURIComponent(name)}/preview?brand=${brand}`)
      .then(async (r) => {
        if (!live) return;
        setSubject(r.headers.get("X-Email-Subject") || "");
        setHtml(await r.text());
      })
      .catch((e) => live && setError(errorMessage(e)));
    return () => {
      live = false;
    };
  }, [name, brand]);
  if (error) return <Alert tone="danger">{error}</Alert>;
  if (html == null) return <Spinner />;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="small muted">
        <FormattedMessage id="admin.emails.subjectLine" values={{ subject, b: (chunks) => <b style={{ color: "var(--text)" }} dir="auto">{chunks}</b> }} />
      </div>
      {/* Sandboxed: no scripts, no same-origin access. */}
      <iframe title={t("admin.emails.previewOf", { name })} sandbox="" srcDoc={html} style={{ width: "100%", height: 720, border: "1px solid var(--border)", borderRadius: 10, background: "#fff" }} />
    </div>
  );
}

export function AdminEmails() {
  const t = useT();
  const intl = useIntlShape();
  const { date, relative } = useFmt();
  const templates = useQuery({ queryKey: ["email-templates"], queryFn: () => unwrap(api.GET("/v1/admin/email/templates")) });
  const [selected, setSelected] = useState<string | null>(null);
  const [brand, setBrand] = useState<"sample" | "none">("sample");
  const [status, setStatus] = useState<StatusFilter>("all");
  const messages = useQuery({
    queryKey: ["email-messages", status],
    queryFn: () =>
      unwrap(api.GET("/v1/admin/email/messages", { params: { query: { status: status === "all" ? undefined : status, limit: 50 } } })),
    refetchInterval: 15_000,
  });
  const items = templates.data?.items ?? [];
  const current = selected ?? items[0]?.name ?? null;
  return (
    <>
      <PageHead title={t("admin.emails.title")} description={t("admin.emails.description")} />
      <div className="stack">
        <div className="split-wide" style={{ gridTemplateColumns: "minmax(0, 300px) minmax(0, 1fr)" }}>
          <Card title={t("admin.emails.templates")} pad={false}>
            {templates.isLoading ? (
              <Spinner />
            ) : templates.error ? (
              <div className="card-body"><Alert tone="danger">{errorMessage(templates.error)}</Alert></div>
            ) : (
              <div className="stage-list">
                {items.map((tpl) => (
                  <button
                    key={tpl.name}
                    type="button"
                    className={cx("stage-item", current === tpl.name && "active")}
                    onClick={() => setSelected(tpl.name)}
                    style={{ gridTemplateColumns: "1fr", textAlign: "start", width: "100%", border: 0, borderBottom: "1px solid var(--border)", cursor: "pointer", background: current === tpl.name ? "var(--primary-soft)" : "transparent" }}
                  >
                    <div>
                      <div className="row" style={{ gap: 6 }}>
                        <code dir="ltr">{tpl.name}</code>
                        {tpl.layout ? (
                          <Badge>{t("admin.emails.layout")}</Badge>
                        ) : tpl.category ? (
                          <Badge tone={tpl.category === "marketing" ? "warn" : tpl.category === "staff" ? "info" : undefined}>{t("admin.emails.category", { category: tpl.category })}</Badge>
                        ) : null}
                      </div>
                      {tpl.trigger && <div className="small muted" dir="auto" style={{ marginTop: 2 }}>{tpl.trigger}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
          <Card
            title={current ? <span dir="ltr">{current}</span> : t("admin.emails.preview")}
            actions={
              <Segmented
                value={brand}
                onChange={setBrand}
                options={[
                  { value: "sample", label: t("admin.emails.brandInstaller") },
                  { value: "none", label: "Solar OS" },
                ]}
              />
            }
          >
            {current ? <Preview name={current} brand={brand} /> : <Empty icon={<Mail />} title={t("admin.emails.noTemplates")} />}
          </Card>
        </div>
        <Card
          title={t("admin.emails.recent")}
          pad={false}
          actions={
            <Segmented<StatusFilter>
              value={status}
              onChange={setStatus}
              options={(["all", "queued", "sent", "failed", "suppressed"] as const).map((s) => ({ value: s, label: t("admin.emails.status", { status: s }) }))}
            />
          }
        >
          {messages.isLoading ? (
            <Spinner />
          ) : messages.error ? (
            <div className="card-body"><Alert tone="danger">{errorMessage(messages.error)}</Alert></div>
          ) : !messages.data!.items.length ? (
            <Empty icon={<Mail />} title={t("admin.emails.noMessages")} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("admin.emails.colTemplate")}</th>
                    <th>{t("admin.emails.colTo")}</th>
                    <th className="hide-sm">{t("admin.emails.colSubject")}</th>
                    <th>{t("admin.emails.colStatus")}</th>
                    <th className="hide-sm">{t("admin.emails.colQueued")}</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.data!.items.map((m) => (
                    <tr key={m.id}>
                      <td><code dir="ltr">{m.template}</code></td>
                      <td className="ellipsis" dir="auto" style={{ maxWidth: 220 }}>{intl.formatList(m.recipients, { type: "unit", style: "short" })}</td>
                      <td className="hide-sm ellipsis" dir="auto" style={{ maxWidth: 320 }}>{m.subject ?? <span className="faint">—</span>}</td>
                      <td>
                        <Badge tone={STATUS_TONE[m.status]}>{t("admin.emails.status", { status: m.status })}</Badge>
                        {m.attempts > 1 && <span className="faint small"> {t("admin.emails.attempts", { count: m.attempts })}</span>}
                        {m.error && <div className="small" dir="auto" style={{ color: "var(--danger)" }} title={m.error}>{m.error.slice(0, 60)}</div>}
                      </td>
                      <td className="hide-sm muted" title={date(m.created_at, true)}>{relative(m.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
